/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {EarningsAccountApiService, EarningsAccountModel} from './earnings-account-api.service';
import {EarningsAccountCopyModalComponent} from './earnings-account-copy-modal.component';
import {EarningsAccountItemPageComponent} from './earnings-account-item-page.component';
import {EarningsAccountListPageComponent} from './earnings-account-list-page.component';

export function earningsAccountObjectType(
  localNetworkProfile: Pick<LocalNetworkProfile, 'getNetworkTranslations'>
): string {
  return localNetworkProfile.getNetworkTranslations('Earnings Account') || 'Earnings Account';
}

export function earningsAccountStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(EarningsAccountApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const localNetworkProfile = adnRouterHelper.services.localNetworkProfile();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.managePublishing.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisherAdmin.apiKey);
  const rh = adnRouterHelper.createRouterHelper('earnings-accounts.earnings-account', EarningsAccountApiService, 'app.inv', 'app');
  const copyConfig = {
    modalComponent: EarningsAccountCopyModalComponent,
    stateUrl: 'app.inv.earnings-accounts.earnings-account',
    lcase: 'earnings account',
    ucase: 'Earnings Account'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: EarningsAccountListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      earningsAccountPage: function($stateParams: any) {
        const blockedMessage = permissions.isAnyAxPublisher()
          ? 'Publisher users cannot change the state of earnings accounts.'
          : undefined;
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<EarningsAccountModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true, readOnly(), api, earningsAccountObjectType(localNetworkProfile), 'name', copyConfig, blockedMessage
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, earningsAccountObjectType(localNetworkProfile), 'name', copyConfig, blockedMessage
        ));
      },
      model: (earningsAccountPage: SignalAdminResourcePageState<EarningsAccountModel>) => earningsAccountPage.model()
    },
    views: {
      'content@app': {component: EarningsAccountItemPageComponent, bindings: {state: 'earningsAccountPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'earningsAccountPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'earningsAccountPage'}}
    }
  }));
  return states.build();
}
